import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { Hourglass } from 'lucide-react';

export const LoginPage: React.FC = () => {
  const auth = useAuth();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Don't auto-redirect if there's an error
    if (auth.error) {
      return;
    }

    // Start countdown
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    // Redirect when countdown reaches 0
    if (countdown === 0) {
      auth.login();
    }
  }, [countdown, auth.error, auth]);

  const handleManualLogin = () => {
    auth.login();
  };

  // Show error state with manual login button
  if (auth.error) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background'>
        <Card className='w-full max-w-2xl'>
          <CardHeader className='space-y-1'>
            <CardTitle className='text-2xl text-center'>Anmeldung fehlgeschlagen</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription>{auth.error.message}</AlertDescription>
            </Alert>

            <Button onClick={handleManualLogin} className='w-full' disabled={auth.isLoading}>
              {auth.isLoading ? 'Anmeldung läuft...' : 'Erneut versuchen'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show redirect state
  return (
    <div className='min-h-screen flex items-center justify-center bg-background'>
      <Card className='w-full max-w-2xl'>
        <CardContent className='pt-8 pb-8'>
          <div className='flex items-start gap-6'>
            <div className='flex-shrink-0'>
              <Hourglass className='w-20 h-20 text-cyan-500' strokeWidth={1.5} />
            </div>
            <div className='flex-1'>
              <CardTitle className='text-2xl mb-4 text-cyan-600'>Sie werden weitergeleitet...</CardTitle>
              <CardDescription className='text-base leading-relaxed text-foreground'>
                Wir nutzen Microsoft Azure für unseren Login-Prozess. Zu diesem Zweck werden Sie in {countdown} Sekunden
                zu einem externen Formular weitergeleitet. Falls die automatische Weiterleitung nicht funktioniert, dann
                klicken Sie bitte{' '}
                <button onClick={handleManualLogin} className='text-cyan-600 hover:text-cyan-700 underline font-medium'>
                  hier
                </button>
                .
              </CardDescription>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
